import { observer } from 'mobx-react-lite';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { BaseButton } from 'office-ui-fabric-react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { isSingleLineListView } from 'owa-mail-layout';
import { getDensityModeString } from 'owa-session-store';
import * as React from 'react';

const styles = require('./Rollup.scss');
const classNames = require('classnames/bind').bind(styles);

interface RollupStyles {
    containerClassName?: string;
    primaryTextClassName?: string;
    secondaryTextClassName?: string;
    dismissButtonClassName?: string;
    dismissButtonIconClassName?: string;
}

export interface RollupProps {
    badgeCount?: number;
    onClick: () => void;
    onDismissed: (evt?: React.MouseEvent<BaseButton>) => void;
    primaryText: string;
    secondaryText: string;
    showOverlay?: boolean;
    styleSelectorAsPerUserSettings: string;
    styles?: RollupStyles;
}

export default observer(function Rollup(props: RollupProps) {
    const {
        badgeCount,
        onClick,
        onDismissed: onDismissed_0,
        primaryText,
        secondaryText,
        showOverlay,
        styleSelectorAsPerUserSettings,
        styles: customStyles = {},
    } = props;

    const {
        primaryTextClassName,
        secondaryTextClassName,
        containerClassName,
        dismissButtonClassName,
        dismissButtonIconClassName,
    } = customStyles;

    const dismissButtonClassNames = classNames(styles.dismissButton, dismissButtonClassName);

    const onDismissed = React.useCallback(
        (evt: React.MouseEvent<BaseButton>) => {
            evt.stopPropagation();
            onDismissed_0(evt);
        },
        [onDismissed_0]
    );

    const dismissButton = (
        <ActionButton
            className={dismissButtonClassNames}
            onClick={onDismissed}
            iconProps={{
                iconName: ControlIcons.Cancel,
                styles: {
                    root: dismissButtonIconClassName,
                },
            }}
        />
    );

    const overlay = showOverlay && <div className={styles.overlay}></div>;

    const renderSingleLineViewRollup = () => {
        return (
            <div
                className={classNames(
                    styles.singleLineViewContainer,
                    styles.slvRollupContainer,
                    containerClassName
                )}
                onClick={onClick}>
                {overlay}
                <div className={classNames(getDensityModeString(), styles.firstRow)}>
                    <div
                        className={classNames(
                            styles.firstColumnNoTwisty,
                            styleSelectorAsPerUserSettings,
                            !badgeCount && styles.firstColumnNoBadge
                        )}>
                        {badgeCount && (
                            <div
                                className={classNames(
                                    styles.unseenCountBadgeContainer,
                                    styleSelectorAsPerUserSettings
                                )}>
                                <div className={styles.badgeOverlaySlv}></div>
                                <span className={styles.unseenCountBadge}>{badgeCount}</span>
                            </div>
                        )}
                        <span className={primaryTextClassName}>{primaryText}</span>
                    </div>
                    <div className={styles.secondColumn}>
                        <div className={secondaryTextClassName}>{secondaryText}</div>
                    </div>
                    {dismissButton}
                </div>
            </div>
        );
    };

    const renderThreeColumnViewRollup = () => {
        return (
            <div
                className={classNames(
                    getDensityModeString(),
                    styles.threeColumnViewContainer,
                    styles.threeColumnRollupContainer,
                    containerClassName
                )}
                onClick={onClick}>
                {overlay}
                {badgeCount && (
                    <div className={styles.firstColumnThreeColumn}>
                        <div
                            className={classNames(
                                styles.unseenCountBadgeContainer,
                                styleSelectorAsPerUserSettings
                            )}>
                            <div className={styles.badgeOverlay}></div>
                            <span className={styles.unseenCountBadge}>{badgeCount}</span>
                        </div>
                    </div>
                )}
                <div
                    className={classNames(
                        styles.column,
                        styleSelectorAsPerUserSettings,
                        !badgeCount && styles.firstColumnNoBadge
                    )}>
                    <div className={primaryTextClassName}>{primaryText}</div>
                    <div className={secondaryTextClassName}>{secondaryText}</div>
                </div>
                {dismissButton}
            </div>
        );
    };

    return isSingleLineListView() ? renderSingleLineViewRollup() : renderThreeColumnViewRollup();
});
